import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePaperTheme } from '@/hooks/usePaperTheme';
import { useAuthStore } from '@/features/auth/store';
import { submitFeedback, type FeedbackCategory } from '@/services/api/feedback';

const CATEGORIES: { value: FeedbackCategory; label: string; hint: string }[] = [
  { value: 'general', label: 'General', hint: 'Anything else.' },
  { value: 'bug', label: 'Bug', hint: 'Something broke or behaved unexpectedly.' },
  { value: 'feature', label: 'Feature request', hint: 'You wish Clyra did this thing.' },
  { value: 'billing', label: 'Billing', hint: 'Payments, plans, refunds.' },
  { value: 'security', label: 'Security', hint: 'Vulnerability or suspicious activity. We respond quickly.' },
];

// SupportPage is the public contact form. Available without login so visitors
// who hit a snag during signup can still reach us. When signed in, name+email
// pre-fill from the session and the backend ties the submission to user_id.
export function SupportPage() {
  usePaperTheme();
  const user = useAuthStore((s) => s.user);

  const [name, setName] = useState(user?.email?.split('@')[0] ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [category, setCategory] = useState<FeedbackCategory>('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isLoggedIn = !!user;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      toast.error('Tell us a little more — at least 10 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await submitFeedback({
        name: name.trim() || (isLoggedIn ? (user!.email) : 'Anonymous'),
        // For logged-in users the backend overrides this anyway; we still send
        // it so the form works for visitors.
        email: email.trim(),
        category,
        message: message.trim(),
      });
      setSubmitted(true);
      setMessage('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not send — please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-charcoal-soft">
      <nav className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
        <Link to="/" className="text-lg font-semibold tracking-tight-ish text-charcoal">
          clyra<span className="text-teal-500">.</span>
        </Link>
        <Link
          to={isLoggedIn ? '/dashboard' : '/login'}
          className="text-petit text-charcoal-muted hover:text-charcoal transition-colors"
        >
          {isLoggedIn ? 'Back to app' : 'Sign in'}
        </Link>
      </nav>

      <main className="mx-auto max-w-2xl px-6 pb-24 pt-8">
        <p className="text-petit font-medium uppercase tracking-[0.14em] text-teal-600">Support</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-display text-charcoal">
          Tell us what's on your mind
        </h1>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-charcoal-muted">
          A real person reads every message. Bug reports, feature requests, billing
          questions, security disclosures — they all land in the same inbox.
          Average response is under a day.
        </p>

        {submitted ? (
          <div className="mt-10 rounded-xl border border-teal-500/30 bg-teal-50 p-6">
            <p className="text-sm font-medium text-teal-700">Thanks — we got it.</p>
            <p className="mt-1 text-petit text-teal-700/80">
              We'll reply to <span className="font-mono">{email}</span> as soon as we can.
            </p>
            <button
              type="button"
              onClick={() => { setSubmitted(false); }}
              className="mt-4 text-petit text-teal-700 hover:text-teal-800 transition-colors"
            >
              Send another message
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-10 space-y-5">
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="sup-name" className="block text-petit font-medium text-charcoal">Your name</label>
                <input
                  id="sup-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-stone-default bg-paper-raised px-3 py-2 text-sm text-charcoal placeholder:text-charcoal-faint focus:border-teal-500/50 focus:outline-none focus:ring-1 focus:ring-teal-500/20"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="sup-email" className="block text-petit font-medium text-charcoal">
                  Email {isLoggedIn && <span className="text-charcoal-faint">(from your account)</span>}
                </label>
                <input
                  id="sup-email"
                  type="email"
                  required
                  disabled={isLoggedIn}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-stone-default bg-paper-raised px-3 py-2 text-sm text-charcoal placeholder:text-charcoal-faint focus:border-teal-500/50 focus:outline-none focus:ring-1 focus:ring-teal-500/20 disabled:bg-paper-soft disabled:text-charcoal-muted"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-petit font-medium text-charcoal">Category</label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCategory(c.value)}
                    className={`rounded-lg border px-3 py-2 text-tiny font-medium transition-colors ${
                      category === c.value
                        ? 'border-teal-500/50 bg-teal-50 text-teal-700'
                        : 'border-stone-default bg-paper-raised text-charcoal-muted hover:border-stone-strong hover:text-charcoal'
                    }`}
                    title={c.hint}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <p className="text-micro text-charcoal-faint">{CATEGORIES.find((c) => c.value === category)?.hint}</p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="sup-msg" className="block text-petit font-medium text-charcoal">Message</label>
              <textarea
                id="sup-msg"
                required
                rows={7}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={category === 'bug'
                  ? 'What did you do, what did you expect, what happened? Include browser + diagram URL if relevant.'
                  : category === 'security'
                  ? 'A short summary is enough — we will reply with our PGP key for full details.'
                  : 'Take your time.'
                }
                className="w-full rounded-lg border border-stone-default bg-paper-raised px-3 py-2 text-sm text-charcoal placeholder:text-charcoal-faint focus:border-teal-500/50 focus:outline-none focus:ring-1 focus:ring-teal-500/20 resize-y"
                minLength={10}
                maxLength={4000}
              />
              <div className="flex justify-between text-micro text-charcoal-faint">
                <span>Minimum 10 characters.</span>
                <span>{message.length} / 4000</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-micro text-charcoal-faint">
                By submitting you agree to our{' '}
                <Link to="/privacy" className="text-teal-600 hover:text-teal-700 transition-colors">privacy policy</Link>.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-charcoal px-4 py-2.5 text-sm font-medium text-paper hover:bg-charcoal-soft transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
